// src/components/ChatDeliveryListener.js
import { useEffect, useRef } from "react";
import { auth, db } from "../firebase";
import { doc, onSnapshot, query, collection, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

/**
 * ✅ ChatDeliveryListener
 * - Watches chats for current user
 * - When a NEW incoming message exists (lastSenderId != me)
 *   it calls markChatDelivered({chatId}) once
 * - This updates chats/{chatId}.lastDeliveredAt.{myUid}
 */
export default function ChatDeliveryListener() {
  const inFlightRef = useRef(new Set()); // prevent spamming same chat

  useEffect(() => {
    let unsubAuth = null;
    let unsubChats = null;

    const functions = getFunctions(undefined, "us-central1");
    const markChatDeliveredFn = httpsCallable(functions, "markChatDelivered");

    unsubAuth = auth.onAuthStateChanged((user) => {
      // cleanup old listener
      if (unsubChats) {
        unsubChats();
        unsubChats = null;
      }
      inFlightRef.current = new Set();

      if (!user?.uid) return;

      const myUid = user.uid;

      // Query by membership (users); hide/delete filtered client-side via visibleTo.
      const qChats = query(
        collection(db, "chats"),
        where("users", "array-contains", myUid)
      );

      unsubChats = onSnapshot(
        qChats,
        (snap) => {
          snap.forEach((d) => {
            const chatId = d.id;
            const chat = d.data() || {};

            if (
              Array.isArray(chat.visibleTo) &&
              !chat.visibleTo.includes(myUid)
            ) {
              return;
            }

            const lastSenderId = chat.lastSenderId || "";
            const lastMessageAtMs =
              chat.lastMessageAt?.toMillis?.() ||
              chat.updatedAt?.toMillis?.() ||
              0;

            // ✅ only for incoming (someone else sent)
            if (!lastSenderId || lastSenderId === myUid) return;
            if (!lastMessageAtMs) return;

            const myDeliveredMs =
              chat.lastDeliveredAt?.[myUid]?.toMillis?.() || 0;

            // ✅ if already delivered for latest message -> skip
            if (myDeliveredMs && myDeliveredMs >= lastMessageAtMs) return;

            // ✅ prevent re-entry spam
            if (inFlightRef.current.has(chatId)) return;
            inFlightRef.current.add(chatId);

            markChatDeliveredFn({ chatId })
              .catch(() => {})
              .finally(() => {
                // release lock shortly (in case of rapid updates)
                setTimeout(() => {
                  const s = new Set(inFlightRef.current);
                  s.delete(chatId);
                  inFlightRef.current = s;
                }, 800);
              });
          });
        },
        (err) => {
          console.error("[ChatDeliveryListener] snapshot error:", err);
        }
      );
    });

    return () => {
      if (unsubChats) unsubChats();
      if (unsubAuth) unsubAuth();
    };
  }, []);

  return null; // no UI
}